import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Shield, UserPlus, LogOut, HandHeart, Mail } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { API } from '../../App';
import { detectPreferences } from './carePreferences';

const getRoleLabel = (role) => {
  if (role === 'patient') return 'Patient';
  if (role === 'care_partner') return 'Care Partner';
  return 'Supporter';
};

// Small, neutral pill next to non-patient members. Same accent color for both
// options — no visual hierarchy, no ranking vibe.
const SupportLevelPill = ({ level, editable, onChange }) => {
  const isActive = level === 'active';
  const label = isActive ? 'Active Support' : 'Community Support';
  const Icon = isActive ? HandHeart : Mail;
  const pill = (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 dark:bg-cyan-900/25 dark:text-cyan-300"
      data-testid={`support-level-pill-${level}`}
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
  if (!editable) return pill;
  return (
    <button
      onClick={onChange}
      title="Tap to change — you can shift this as your capacity changes."
      className="rounded-full focus:outline-none focus:ring-2 focus:ring-cyan-400"
      data-testid="support-level-pill-toggle"
    >
      {pill}
    </button>
  );
};

export const CircleInfoDialog = ({ open, onOpenChange, circle, onLogout, userName, currentUserId, isPatient }) => {
  const [members, setMembers] = useState([]);
  const [togglingRole, setTogglingRole] = useState(null);
  const [togglingLevel, setTogglingLevel] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientFirstName, setRecipientFirstName] = useState('');
  const [invitingRecipient, setInvitingRecipient] = useState(false);
  const [patientVibes, setPatientVibes] = useState([]);

  const inviteMsg = `Hi! ${userName || 'Someone'} has invited you to be in their ${circle?.name || 'care circle'}. They are using an app called CircleUpSquad to coordinate their helpers. Sign up with this link and you'll be automatically added:\n\nhttps://circleupsquad.com/auth?code=${circle?.invite_code}`;

  const pendingRecipient = members.find(
    (m) => (m.role === 'patient' || m.circle_role === 'patient') && m.user?.is_pending
  );

  const fetchMembers = () => {
    if (circle?.id) {
      axios.get(`${API}/circles/${circle.id}/members`).then((r) => setMembers(r.data)).catch(() => {});
      axios
        .get(`${API}/circles/${circle.id}/guidance`)
        .then((r) => setPatientVibes(detectPreferences(r.data?.support_this_week || '')))
        .catch(() => setPatientVibes([]));
    }
  };

  useEffect(() => {
    if (open) fetchMembers();
  }, [open, circle?.id]);

  const inviteRecipient = async () => {
    if (!recipientEmail.trim()) {
      toast.error('Add their email so we can send the invite.');
      return;
    }
    setInvitingRecipient(true);
    try {
      await axios.post(`${API}/circles/${circle.id}/invite-recipient`, {
        email: recipientEmail.trim(),
        first_name: recipientFirstName.trim() || undefined,
      });
      toast.success(`Invite sent to ${recipientEmail.trim()}`);
      setRecipientEmail('');
      setRecipientFirstName('');
      fetchMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send invite');
    }
    setInvitingRecipient(false);
  };

  const toggleMyLevel = async (currentLevel) => {
    if (!circle?.id) return;
    const next = currentLevel === 'active' ? 'community' : 'active';
    setTogglingLevel(true);
    try {
      await axios.put(`${API}/circles/${circle.id}/my-support-level`, { support_level: next });
      toast.success(next === 'active' ? "Switched to Active Support 🤝" : "Switched to Community Support 💌");
      fetchMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    }
    setTogglingLevel(false);
  };

  const toggleRole = async (userId, currentRole) => {
    const newRole = currentRole === 'care_partner' ? 'member' : 'care_partner';
    setTogglingRole(userId);
    try {
      const res = await axios.put(`${API}/circles/${circle.id}/members/${userId}/role`, { role: newRole });
      toast.success(res.data.message);
      fetchMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update role');
    }
    setTogglingRole(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="surface-card max-w-sm max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Circle Settings</DialogTitle>
          <DialogDescription>{circle?.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {pendingRecipient && (
            <div
              className="surface-card rounded-xl p-4 border-2 border-amber-100 dark:border-amber-900/40 bg-gradient-to-br from-amber-50/50 to-transparent dark:from-amber-950/20 space-y-3"
              data-testid="pending-recipient-section"
            >
              <div>
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide">Care Recipient</p>
                <p className="text-sm font-bold text-foreground mt-1">{pendingRecipient.user?.name} hasn{"'"}t joined yet</p>
                <p className="text-xs text-muted-foreground">Send them a link to set up their own account when they{"'"}re ready.</p>
              </div>
              <div className="space-y-2">
                <Input placeholder="Their first name (optional)" value={recipientFirstName} onChange={(e) => setRecipientFirstName(e.target.value)} data-testid="pending-recipient-name-input" />
                <Input type="email" placeholder="Their email address" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} data-testid="pending-recipient-email-input" required />
                <Button onClick={inviteRecipient} disabled={invitingRecipient} className="w-full btn-primary" data-testid="pending-recipient-invite-btn">
                  {invitingRecipient ? 'Sending invite...' : <>Send activation link →</>}
                </Button>
              </div>
            </div>
          )}

          <div className="surface-card rounded-xl p-4 space-y-3" data-testid="circle-members-list">
            <p className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Squad Members ({members.length})
            </p>
            {members.length > 0 ? (
              <div className="space-y-2.5">
                {members.map((m, i) => {
                  const memberUserId = m.user?.id || m.user_id;
                  const isMe = memberUserId === currentUserId;
                  // Designations are stripped from the API response for non-admin viewers
                  // unless it's their own row. Only render role/pill when the field is present.
                  const hasRole = Boolean(m.role || m.circle_role);
                  const role = m.role || m.circle_role || 'member';
                  const isPatientRow = role === 'patient';
                  const hasLevel = Boolean(m.support_level);
                  const level = m.support_level || 'active';
                  return (
                    <div key={memberUserId || `m-${i}`} className="flex items-center gap-2.5">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${
                          hasRole && role === 'care_partner' ? 'bg-gradient-to-br from-amber-500 to-orange-500' : 'bg-gradient-to-br from-cyan-500 to-emerald-500'
                        }`}
                      >
                        {m.user?.name?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{m.user?.name || 'Unknown'}{isMe && <span className="text-muted-foreground/60 font-normal"> · you</span>}</p>
                        <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                          {hasRole && (
                            <span
                              className={`text-[10px] font-medium ${
                                role === 'patient' ? 'text-cyan-600' : role === 'care_partner' ? 'text-amber-600' : 'text-muted-foreground'
                              }`}
                            >
                              {role === 'care_partner' && <Shield className="w-3 h-3 inline mr-0.5" />}
                              {getRoleLabel(role)}
                            </span>
                          )}
                          {isPatientRow && patientVibes.slice(0, 2).map((v) => (
                            <span
                              key={v.label}
                              className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300"
                              title={v.label}
                              data-testid={`patient-vibe-${v.label.slice(0, 12).replace(/\s/g, '-').toLowerCase()}`}
                            >
                              <span aria-hidden="true">{v.emoji}</span>
                              <span className="max-w-[110px] truncate">{v.label.replace(/\.$/, '')}</span>
                            </span>
                          ))}
                          {hasLevel && !isPatientRow && role !== 'care_partner' && (
                            <SupportLevelPill
                              level={level}
                              editable={isMe && !togglingLevel}
                              onChange={() => toggleMyLevel(level)}
                            />
                          )}
                        </div>
                      </div>
                      {isPatient && hasRole && !isPatientRow && (
                        <button
                          onClick={() => toggleRole(memberUserId, role)}
                          disabled={togglingRole === memberUserId}
                          className={`text-[10px] px-2 py-1 rounded-lg font-medium transition-colors ${
                            role === 'care_partner'
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
                          }`}
                          data-testid={`toggle-role-${m.user?.id}`}
                        >
                          {role === 'care_partner' ? 'Remove CP' : 'Make Care Partner'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Loading...</p>
            )}
          </div>

          {circle?.invite_code && (
            <div className="surface-card rounded-xl p-4 text-center space-y-2">
              <p className="text-xs text-muted-foreground">Share this code to invite people:</p>
              <p className="text-2xl font-mono font-bold text-foreground tracking-wider" data-testid="circle-code-display">{circle.invite_code}</p>
              <Button
                className="btn-primary w-full mt-2"
                onClick={() => { onOpenChange(false); setTimeout(() => document.querySelector('[data-testid="header-invite-btn"]')?.click(), 200); }}
                data-testid="settings-open-invite-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Invite people
              </Button>
            </div>
          )}
          <div className="surface-card rounded-xl p-4 space-y-2">
            <p className="text-xs text-muted-foreground">Preview of what gets copied:</p>
            <p className="text-xs text-foreground leading-relaxed whitespace-pre-line">{inviteMsg}</p>
          </div>
          <button onClick={onLogout} className="w-full flex items-center justify-center gap-2 p-3 text-sm text-rose-500 hover:text-rose-600 transition-colors" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
